import { localize, resolveConnectNoun } from "@ando/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import type * as React from "react";

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";

import type { CatalogEntry } from "@/features/connectors";
import { CapabilityBadge } from "@/features/connectors/components";

import { type ConnectionStatus, StatusPill } from "./StatusElements";

/**
 * Page header (breadcrumb + hero + status pill + primary CTA) for the
 * per-capability route. Pure presentational: every callback / link target
 * is supplied by the route.
 *
 * Extracted from ``$capability.tsx`` on May 28, 2026. DOM is byte-equivalent
 * to the inline ``<nav>`` + ``<header>`` block.
 */
export interface CapabilityHeaderProps {
  readonly entry: CatalogEntry;
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly status: ConnectionStatus;
  readonly connectionCount: number;
  readonly capabilityGranted: boolean;
  readonly upgradeCapability: string | null;
}

export const CapabilityHeader: React.FC<CapabilityHeaderProps> = ({
  entry,
  spec,
  status,
  connectionCount,
  capabilityGranted,
  upgradeCapability,
}) => {
  const { t, i18n } = useLingui();
  return (
    <>
      {/* Breadcrumb */}
      <nav aria-label={t`Breadcrumb`}>
        <Link
          to="/integrations"
          className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-ando-ink/[0.04] px-2.5 py-1.5 font-medium text-[12px] text-ink-2 transition-colors hover:bg-ando-ink/[0.07] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
        >
          <ArrowLeft size={14} strokeWidth={2} aria-hidden />
          <Trans>Tutte le integrazioni</Trans>
        </Link>
      </nav>

      {/* ── Header hero ────────────────────────────────────────────────── */}
      <header className="flex items-start gap-3">
        <CapabilityBadge entry={entry} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
            Workspace
          </div>
          <h1 className="font-display font-semibold text-[20px] text-ink-1 leading-tight tracking-tight">
            {localize(spec.title, i18n)}
          </h1>
          <p className="mt-0.5 text-[13px] text-ink-2">{localize(spec.subtitle, i18n)}</p>
          {/* Status pill — only the actionable warning. The connected count is
              already shown by the "Account collegati" section header below, so a
              healthy/empty pill here would just repeat it (redundant + extra height). */}
          {status === "needs_reauth" ? (
            <div className="mt-2 flex items-center gap-2">
              <StatusPill status={status} count={connectionCount} noun={resolveConnectNoun(spec)} />
            </div>
          ) : null}
        </div>
        {!capabilityGranted && upgradeCapability ? (
          <div className="shrink-0">
            <Link
              to="/integrations/$capability"
              params={{ capability: upgradeCapability }}
              className="inline-flex items-center gap-1.5 rounded-md bg-warning/15 px-3 py-2 font-medium text-[12px] text-warning hover:bg-warning/20"
            >
              <Trans>Piano superiore richiesto</Trans>
            </Link>
          </div>
        ) : null}
      </header>
    </>
  );
};
CapabilityHeader.displayName = "CapabilityHeader";
